<template>
  <v-card class="py-4">
    <v-card-title class="pb-0">
      Add component
    </v-card-title>
    <v-card-text class="mt-0 pt-0">
      <span class="caption">
      This allows you to include a meal component into this recipe.  You can choose between the base component or a modified one from a meal (aka the meal version).
      The ingredient amounts will be adjusted so that the total weight of the component added to the meal is the value in
        "Amount" <span v-if="adjustedAmount">({{ adjustedAmount }}g)</span>.
      </span>
    </v-card-text>
    <v-card-text>
      <v-toolbar flat>
        <ComponentAutocomplete
          label="Component"
          :component.sync="componentToAdd"
        />
        <v-spacer/>
        <v-select
          class="pl-2"
          clearable
          dense
          label="Meal Version"
          :items="componentMeals"
          item-value="id"
          item-text="name"
          v-model="selectedComponentMeal"
          :hint="selectedMealHint"
          return-object
        >
          <template slot="item" slot-scope="data">
            {{ data.item.date }} - {{ data.item.name }}({{ data.item.id }})
          </template>

        </v-select>
        <v-text-field
          class="table-input pl-2"
          label="Amount"
          dense
          type="number"
          v-model="adjustedAmount"
          suffix="g"
        />

        <v-btn outlined text class="ml-2 mb-4" @click="handleAddComponent">Add to Meal</v-btn>
      </v-toolbar>


      <v-alert dense type="warning" v-if="componentToAdd && selectedComponentIngredients.length === 0">
        no ingredients, perhaps try a meal version
      </v-alert>

      <MealDetailTable2
        v-if="componentToAdd && selectedComponentIngredients.length > 0"
        :meal-id="meal.id"
        :component="componentToAdd"
        :ingredients="selectedComponentIngredients"
        :read-only="true"
      />

    </v-card-text>
  </v-card>
</template>

<script>
import ComponentAutocomplete from '@/components/ComponentAutocomplete';
import MealDetailTable2 from '@/components/MealDetailTable2';
import {convertIngredient, formatWeight, normaliseIngredient} from '@/store/utils';
import api from '@/api';

export default {
  name: 'AddComponentCard2',
  components: {
    ComponentAutocomplete,
    MealDetailTable2
  },
  props: {
    meal: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      componentToAdd: null,
      selectedComponentMeal: null,
      componentMealIds: null,
      componentMeals: [],
      selectedAmount: null,
      adjustedAmount: null,
    }
  },
  watch: {
    async componentToAdd(c) {
      if (c && c.meal_foods) {
        this.componentMealIds = [...new Set(c.meal_foods.map(m => m.mealid))];
        const componentMealIds = this.componentMealIds.sort((a, b) => b - a).slice(0, 100);
        console.log('meal ids', componentMealIds);
        this.componentMeals = await Promise.all(componentMealIds.map(mealId => this.fetchMeal(mealId)));
        this.componentMeals = this.componentMeals.sort((b, a) => a.id - b.id);
        console.log('watch this.componentMeals', this.componentMeals);
      }
      this.selectedComponentMeal = null;
    },
    selectedComponentIngredients(i) {
      console.log('watch selectedComponentIngredients', i);
      this.selectedAmount = formatWeight(i.reduce((a, i) => i.cookedAmount + a, 0));
      this.adjustedAmount = this.selectedAmount;
    },
  },
  computed: {
    selectedComponentIngredients() {
      if (!this.componentToAdd) {
        return [];
      }
      const foods = this.selectedComponentMeal
        ? this.componentToAdd.meal_foods.filter(m => m.mealid === this.selectedComponentMeal.id)
        : this.componentToAdd.component_foods;

      return foods
        .map(normaliseIngredient(this.meal))
        .map(i => ({
          ...i,
          component: this.componentToAdd
        })).map(convertIngredient);
    },
    selectedMealHint() {
      let meal = this.selectedComponentMeal;
      if (meal) {
        return [meal.sku, meal.id, meal.date].join(' | ');
      } else
        return ''
    },
  },
  methods: {
    async fetchMeal(mealId) {
      try {
        const response = await api.get(`v2/meal/${mealId}`);
        return response.data;
      } catch (e) {
        console.error('failed to fetch meal', e);
        return null;
      }
    },
    async replaceIngredientFood({oldIngredient, newIngredient}) {
      function showDetail(i) {
        return i && `${i.name} ${i.foodid} ${i.component_id} `
      }

      console.log(`updateIngredientFood - replacing ${showDetail(oldIngredient)} with ${showDetail(newIngredient)}`, {
        oldIngredient,
        newIngredient
      });

      // Delete old ingredient if it exists
      if (oldIngredient && oldIngredient.foodid) {
        await this.deleteMealIngredient(oldIngredient);
      }

      // Add new ingredient if it has a foodid
      if (newIngredient && newIngredient.foodid) {
        const createPayload = {
          componentId: newIngredient.component_id,
          portionBasis: newIngredient.portion_basis,
          portionMultiplier: newIngredient.portion_multiplier,
          shoppingmultiplier: newIngredient.shoppingmultiplier
        };

        console.log(`api post meals/${newIngredient.mealid}/${newIngredient.foodid}`, {createPayload});
        let response;
        if (newIngredient.mealid) {
          response = await api.post(`meals/${newIngredient.mealid}/${newIngredient.foodid}`, createPayload);
          if (response.data) {
            console.log('api response', response.data);
            const [{id}] = response.data;
            return {id, ...newIngredient};
          } else {
            console.warn('no api response!');
          }
        } else if (newIngredient.component_id) {
          response = await api.post(`v2/component/${newIngredient.component_id}/ingredient/${newIngredient.foodid}`, createPayload);
          if (response.data) {
            console.log('api response', response.data);
            return newIngredient;
          }
        }
      } else {
        console.log('no api call yet as foodid not set');
      }

      return newIngredient;
    },
    async deleteMealIngredient(ingredient) {
      console.log('deleting', ingredient);
      if (ingredient.mealid) {
        const deleteUrl = (ingredient.component_id)
          ? `meals/${ingredient.mealid}/${ingredient.foodid}?component=${ingredient.component_id}`
          : `meals/${ingredient.mealid}/${ingredient.foodid}`

        return await api.delete(deleteUrl);
      } else {
        return await api.delete(`v2/component/${ingredient.component_id}/ingredients/${ingredient.foodid}`);
      }
    },
    async handleAddComponent() {
      console.log('adding component', this.componentToAdd);
      console.log('adding ingredients', this.selectedComponentIngredients);
      const componentIngredients = this.selectedComponentIngredients
        .map(i => ({mealid: this.meal.id, ...i}));
      console.log('adding ingredients', componentIngredients);

      if (this.selectedAmount !== this.adjustedAmount) {
        const ratio = this.adjustedAmount / this.selectedAmount;
        componentIngredients.forEach(i => i.portion_multiplier = i.portion_multiplier * ratio);
      }

      await Promise.all(
        componentIngredients
          .map(i => {
              return this.replaceIngredientFood({newIngredient: i});
            }
          ));

      this.componentToAdd = null;
      console.log('this.meal before emit', JSON.parse(JSON.stringify(this.meal)));

      // Emit event to parent to trigger refresh
      this.$emit('component-added');
    },
  }
}
</script>

<style scoped>
.table-input {
  height: 45px;
  width: 60px;
  font-size: 14px;
}
</style>

